/**
 * <isaboko-facet-rail> — arrow controls for the filter chip rail.
 *
 * Everything about which chips exist and which are active is decided on the
 * server; this class only handles what markup cannot. See
 * snippets/isaboko-facet-rail.liquid for the rest.
 *
 * The rail lives inside #ProductGridContainer, so facets.js throws it away and
 * rebuilds it from the server on every filter change. Nothing can be kept on
 * the instance, and two things a shopper would otherwise lose have to survive
 * that: where they had scrolled the rail to, and which chip their keyboard was
 * on. Both are parked on the class, keyed by element id, and read back when the
 * replacement element connects.
 */
class IsabokoFacetRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.isaboko-facet-rail__track');
    if (!this.track) return;

    this.arrows = this.querySelectorAll('.isaboko-facet-rail__arrow');
    this.onScroll = this.handleScroll.bind(this);
    this.onArrowClick = this.scrollByPage.bind(this);
    this.onChipActivate = this.rememberFocus.bind(this);

    this.arrows.forEach((arrow) => arrow.addEventListener('click', this.onArrowClick));
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('click', this.onChipActivate);

    // Before update(), so the arrows are judged against where the track has
    // actually been put rather than where it started.
    this.restoreScroll();

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onScroll);
      this.resizeObserver.observe(this.track);
    }

    this.update();
    this.restoreFocus();
  }

  disconnectedCallback() {
    this.arrows?.forEach((arrow) => arrow.removeEventListener('click', this.onArrowClick));
    this.track?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('click', this.onChipActivate);
    this.resizeObserver?.disconnect();
  }

  /**
   * Put the track back where the shopper left it.
   *
   * Assigning scrollLeft obeys the stylesheet's `scroll-behavior: smooth`, so
   * doing it plainly animates the restore — the rail visibly sliding in from
   * the first chip on every filter change — and, because the assignment then
   * has not landed yet, leaves scrollLeft still reading 0 for the code that
   * runs next. Turning the behaviour off around the assignment makes it apply
   * synchronously, which is what a restore wants: no animation, and a real
   * value to read immediately after.
   */
  restoreScroll() {
    const saved = IsabokoFacetRail.scrollOffsets.get(this.id);
    if (!saved) return;

    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = saved;
    this.track.style.scrollBehavior = '';
  }

  /**
   * The chip a keyboard user activates is destroyed under them by the rebuild,
   * dropping focus to <body> and sending them back to the top of the page to
   * tab in again. Chips carry a data-facet-key that outlives the rebuild — the
   * same filter and value produce the same key whether the chip is on or off —
   * so the replacement can be found and focused.
   */
  rememberFocus(event) {
    const chip = event.target.closest('.isaboko-facet-rail__chip');
    if (chip && this.id) IsabokoFacetRail.pendingFocus.set(this.id, chip.dataset.facetKey);
  }

  restoreFocus() {
    const key = IsabokoFacetRail.pendingFocus.get(this.id);
    if (!key) return;
    IsabokoFacetRail.pendingFocus.delete(this.id);

    // Compared in JS rather than through an attribute selector: the key carries
    // a filter value, which for a taxonomy filter is a gid:// URI.
    const chip = [...this.querySelectorAll('.isaboko-facet-rail__chip')].find((c) => c.dataset.facetKey === key);

    // preventScroll because the rail is already where the shopper is looking —
    // nothing should move under them just because focus came back. If the chip
    // is gone entirely (its last matching product was filtered away) focus is
    // left alone, which is no worse than not restoring it at all.
    chip?.focus({ preventScroll: true });
  }

  handleScroll() {
    if (this.id) IsabokoFacetRail.scrollOffsets.set(this.id, this.track.scrollLeft);
    this.update();
  }

  /**
   * Show the arrows only when there is something to scroll to, and dim the one
   * whose end has been reached. The 1px slack absorbs the sub-pixel widths that
   * fractional zoom and scaled fonts produce, which otherwise leave an arrow
   * live at a hard stop.
   *
   * Deliberately records nothing. It runs once on connect, before the shopper
   * has scrolled anything, and writing then would put that starting value over
   * the offset restoreScroll() had just put back.
   */
  update() {
    if (!this.track) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const overflows = scrollWidth - clientWidth > 1;

    this.arrows.forEach((arrow) => {
      arrow.hidden = !overflows;
      const atEnd =
        arrow.dataset.direction === 'prev' ? scrollLeft <= 1 : scrollLeft >= scrollWidth - clientWidth - 1;
      arrow.disabled = atEnd;
    });
  }

  scrollByPage(event) {
    const direction = event.currentTarget.dataset.direction === 'prev' ? -1 : 1;
    // Not a full width: leaving a chip or so on screen keeps a landmark, so a
    // shopper can tell the rail moved rather than wondering if it changed.
    //
    // No `behavior` here on purpose. Passing one overrides the stylesheet, and
    // with it the reduced-motion rule that turns the animation off — the sole
    // place that preference is honoured. Omitting it defers to the CSS.
    this.track.scrollBy({ left: direction * this.track.clientWidth * 0.8 });
  }
}

IsabokoFacetRail.scrollOffsets = new Map();
IsabokoFacetRail.pendingFocus = new Map();

customElements.define('isaboko-facet-rail', IsabokoFacetRail);
